/**
 * App-level useAuth hook
 *
 * Wraps the @platform/auth useAuth hook and passes the app's singleton
 * Supabase client to ensure all components share the same session state.
 *
 * When VITE_AUTH_BYPASS=true (staging), returns a mock user so protected
 * pages can render without a real Supabase session.
 *
 * ALWAYS import useAuth from this file, not from @platform/auth/client directly.
 */

import { useAuth as useAuthBase } from "@platform/auth/client";
import { supabase } from "@/lib/supabase";
import type { AuthState, AuthActions } from "@platform/auth";

const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === "true";

const MOCK_USER = {
  id: "00000000-0000-0000-0000-000000000000",
  email: "test@staging.tekdoneright.com",
  role: "platform_admin" as const,
  created_at: new Date().toISOString(),
};

const mockAuth: AuthState & AuthActions = {
  user: MOCK_USER,
  session: {
    access_token: "mock-access-token",
    refresh_token: "mock-refresh-token",
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    user: MOCK_USER,
  },
  loading: false,
  error: null,
  signInWithEmail: async () => ({ success: true, message: "Auth bypassed" }),
  signInWithOAuth: async () => ({ url: "/dashboard" }),
  signUp: async () => ({ success: true, message: "Auth bypassed" }),
  resetPassword: async () => ({ success: true, message: "Auth bypassed" }),
  signOut: async () => {},
  refreshSession: async () => true,
};

export function useAuth() {
  const real = useAuthBase(supabase);
  if (AUTH_BYPASS) return mockAuth;
  return real;
}
